// Hideo - animated "aurora" background of the profile picker (inspired by the PlayStation background waves).
// Waves, threads and glows in the main colour (--brand); everything else is the theme background (--bg of #profilePicker,
// light or dark). It runs only while the profile picker is on screen (ProfilePicker.vue starts it and stops it); with
// the system's "reduce motion" it draws one still frame.
import { isDarkTheme } from './theme'
import { reducedMotion } from '../utils/media'

/** Animation time in seconds: it goes on from where it was when the picker comes back */
let time = 0
// Translucent bands: height (fraction of the screen), wave amplitude and frequency, speed, thickness, phase, opacity
const BANDS = [
  { y: 0.60, amp: 0.07, f: 0.55, s: 0.22, th: 0.11, p: 0.0, a: 0.13 },
  { y: 0.66, amp: 0.09, f: 0.40, s: -0.16, th: 0.16, p: 2.1, a: 0.10 },
  { y: 0.72, amp: 0.06, f: 0.80, s: 0.30, th: 0.08, p: 4.3, a: 0.12 },
  { y: 0.55, amp: 0.05, f: 0.95, s: -0.26, th: 0.06, p: 5.7, a: 0.08 }
]

/** Starts the animation on the canvas; returns the function that stops it */
export function startAurora(cv: HTMLCanvasElement): () => void {
  let raf = 0, last = performance.now()
  const frame = (now: number) => {
    time += Math.min(now - last, 50) / 1000
    last = now
    drawAurora(cv, time)
    raf = reducedMotion() ? 0 : requestAnimationFrame(frame)
  }
  raf = requestAnimationFrame(frame)
  return () => { if (raf) cancelAnimationFrame(raf); raf = 0 }
}

/** Draws one frame at the given time (seconds) */
function drawAurora(cv: HTMLCanvasElement, time: number): void {
  const dpr = Math.min(window.devicePixelRatio || 1, 1.5), w = cv.clientWidth, h = cv.clientHeight
  if (!w || !h) return
  if (cv.width !== Math.round(w * dpr) || cv.height !== Math.round(h * dpr)) { cv.width = Math.round(w * dpr); cv.height = Math.round(h * dpr) }
  const g = cv.getContext('2d')
  if (!g) return
  g.setTransform(dpr, 0, 0, dpr, 0, 0); g.clearRect(0, 0, w, h)
  const rgb = getComputedStyle(document.documentElement).getPropertyValue('--brand').trim().split(/\s+/).join(',') || '232,93,78'
  const dark = isDarkTheme(), k = dark ? 1 : 0.9
  const col = (a: number) => `rgba(${rgb},${(a * k).toFixed(3)})`
  // On the dark theme colours add up (glow), on the light one they overlap normally
  g.globalCompositeOperation = dark ? 'lighter' : 'source-over'

  // Soft glows that move slowly (the actual "aurora")
  const big = Math.max(w, h)
  ;[[0.25, 0.30, 0.13, 0.21, 0.55, 0.20], [0.78, 0.45, -0.11, 0.17, 0.45, 0.14]].forEach(([x, y, sx, sy, r, a], i) => {
    const cx = w * (x + 0.12 * Math.sin(time * sx + i)), cy = h * (y + 0.07 * Math.sin(time * sy + i * 2))
    const gr = g.createRadialGradient(cx, cy, 0, cx, cy, big * r)
    gr.addColorStop(0, col(a)); gr.addColorStop(1, col(0))
    g.fillStyle = gr; g.fillRect(0, 0, w, h)
  })

  // Wavy bands with a bright top edge
  const step = Math.max(8, w / 120)
  BANDS.forEach(b => {
    const top: [number, number][] = [], bot: [number, number][] = []
    for (let x = 0; x <= w + step; x += step) {
      const u = x / w
      const yc = h * (b.y + b.amp * Math.sin(u * Math.PI * 2 * b.f + time * b.s + b.p) + 0.025 * Math.sin(u * Math.PI * 5 - time * b.s * 1.6 + b.p))
      const th = h * b.th * (0.55 + 0.45 * Math.sin(u * Math.PI * 1.6 + time * 0.35 + b.p))
      top.push([x, yc - th / 2]); bot.push([x, yc + th / 2])
    }
    const y0 = h * (b.y - b.amp - b.th), y1 = h * (b.y + b.amp + b.th), gr = g.createLinearGradient(0, y0, 0, y1)
    gr.addColorStop(0, col(b.a * 0.3)); gr.addColorStop(0.5, col(b.a)); gr.addColorStop(1, col(0))
    g.beginPath(); top.forEach(([x, y], i) => (i ? g.lineTo(x, y) : g.moveTo(x, y)))
    for (let i = bot.length - 1; i >= 0; i--) g.lineTo(bot[i][0], bot[i][1])
    g.closePath(); g.fillStyle = gr; g.fill()
    g.beginPath(); top.forEach(([x, y], i) => (i ? g.lineTo(x, y) : g.moveTo(x, y)))
    g.strokeStyle = col(b.a * 2.6); g.lineWidth = 1.2; g.stroke()
  })

  // Thin swaying threads (like the lines of the PlayStation background)
  for (let i = 0; i < 6; i++) {
    g.beginPath()
    for (let x = 0; x <= w + step; x += step) {
      const u = x / w, y = h * (0.63 + 0.06 * Math.sin(u * Math.PI * (1.1 + i * 0.12) + time * (0.2 + i * 0.03) + i * 1.3) + 0.02 * Math.sin(u * Math.PI * 4 - time * 0.5 + i))
      if (x) g.lineTo(x, y); else g.moveTo(x, y)
    }
    g.strokeStyle = col(0.18 - i * 0.02); g.lineWidth = 1; g.stroke()
  }
  g.globalCompositeOperation = 'source-over'
}
